import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ChevronDown, Search, Check, RefreshCw } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/utils/api";

// Popular currencies shown at top
const POPULAR_CURRENCIES = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
];

// All supported currencies
const ALL_CURRENCIES = [
  // Major
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "CHF", symbol: "Fr", name: "Swiss Franc" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "NZD", symbol: "NZ$", name: "New Zealand Dollar" },
  // Asia
  { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
  { code: "HKD", symbol: "HK$", name: "Hong Kong Dollar" },
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "KRW", symbol: "₩", name: "South Korean Won" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
  { code: "TWD", symbol: "NT$", name: "Taiwan Dollar" },
  { code: "THB", symbol: "฿", name: "Thai Baht" },
  { code: "MYR", symbol: "RM", name: "Malaysian Ringgit" },
  { code: "IDR", symbol: "Rp", name: "Indonesian Rupiah" },
  { code: "PHP", symbol: "₱", name: "Philippine Peso" },
  { code: "VND", symbol: "₫", name: "Vietnamese Dong" },
  { code: "PKR", symbol: "Rs", name: "Pakistani Rupee" },
  { code: "BDT", symbol: "৳", name: "Bangladeshi Taka" },
  { code: "LKR", symbol: "Rs", name: "Sri Lankan Rupee" },
  { code: "NPR", symbol: "Rs", name: "Nepalese Rupee" },
  // Middle East
  { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
  { code: "SAR", symbol: "﷼", name: "Saudi Riyal" },
  { code: "QAR", symbol: "ر.ق", name: "Qatari Riyal" },
  { code: "KWD", symbol: "د.ك", name: "Kuwaiti Dinar" },
  { code: "BHD", symbol: "د.ب", name: "Bahraini Dinar" },
  { code: "OMR", symbol: "ر.ع.", name: "Omani Rial" },
  { code: "JOD", symbol: "د.ا", name: "Jordanian Dinar" },
  { code: "ILS", symbol: "₪", name: "Israeli Shekel" },
  { code: "TRY", symbol: "₺", name: "Turkish Lira" },
  // Europe
  { code: "NOK", symbol: "kr", name: "Norwegian Krone" },
  { code: "SEK", symbol: "kr", name: "Swedish Krona" },
  { code: "DKK", symbol: "kr", name: "Danish Krone" },
  { code: "PLN", symbol: "zł", name: "Polish Zloty" },
  { code: "CZK", symbol: "Kč", name: "Czech Koruna" },
  { code: "HUF", symbol: "Ft", name: "Hungarian Forint" },
  { code: "RON", symbol: "lei", name: "Romanian Leu" },
  { code: "BGN", symbol: "лв", name: "Bulgarian Lev" },
  { code: "UAH", symbol: "₴", name: "Ukrainian Hryvnia" },
  { code: "RUB", symbol: "₽", name: "Russian Ruble" },
  // Africa
  { code: "ZAR", symbol: "R", name: "South African Rand" },
  { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
  { code: "EGP", symbol: "E£", name: "Egyptian Pound" },
  { code: "KES", symbol: "KSh", name: "Kenyan Shilling" },
  { code: "GHS", symbol: "₵", name: "Ghanaian Cedi" },
  { code: "MAD", symbol: "د.م.", name: "Moroccan Dirham" },
  { code: "TND", symbol: "د.ت", name: "Tunisian Dinar" },
  // Americas
  { code: "MXN", symbol: "MX$", name: "Mexican Peso" },
  { code: "BRL", symbol: "R$", name: "Brazilian Real" },
  { code: "ARS", symbol: "$", name: "Argentine Peso" },
  { code: "CLP", symbol: "CLP$", name: "Chilean Peso" },
  { code: "COP", symbol: "COL$", name: "Colombian Peso" },
  { code: "PEN", symbol: "S/", name: "Peruvian Sol" },
].sort((a, b) => a.name.localeCompare(b.name));

const CurrencySelector = ({ userCurrency, setUserCurrency, user, compact = false }) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const filteredCurrencies = search
    ? ALL_CURRENCIES.filter(
        (c) =>
          c.code.toLowerCase().includes(search.toLowerCase()) ||
          c.name.toLowerCase().includes(search.toLowerCase())
      )
    : ALL_CURRENCIES;

  const handleSelect = async (currency) => {
    const newCurrency = {
      code: currency.code,
      symbol: currency.symbol,
      country: userCurrency?.country || "US",
    };

    // Update local state immediately
    setUserCurrency(newCurrency);
    localStorage.setItem("userCurrency", JSON.stringify(newCurrency));
    localStorage.setItem("userCurrencyTime", Date.now().toString());

    // If user is logged in, save to profile
    if (user) {
      setSaving(true);
      try {
        await api.put("/users/currency-preference", {
          currency_code: currency.code,
        });
      } catch (error) {
        console.error("Failed to save currency preference:", error);
      } finally {
        setSaving(false);
      }
    }

    setOpen(false);
    setSearch("");
  };

  const handleAutoDetect = async () => {
    // Clear stored currency to force re-detection
    localStorage.removeItem("userCurrency");
    localStorage.removeItem("userCurrencyTime");
    
    // If logged in, clear profile preference
    if (user) {
      try {
        await api.put("/users/currency-preference", {
          currency_code: null,
        });
      } catch (error) {
        console.error("Failed to clear currency preference:", error);
      }
    }
    
    // Reload to trigger auto-detection
    window.location.reload();
  };

  if (compact) {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-slate-600 hover:text-emerald-600"
          >
            <span className="font-medium">{userCurrency?.symbol || "$"}</span>
            <span className="ml-1 text-xs">{userCurrency?.code || "USD"}</span>
            <ChevronDown className="ml-1 h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <div className="p-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search currency..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
          </div>
          <DropdownMenuSeparator />
          
          {!search && (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground">Popular</DropdownMenuLabel>
              {POPULAR_CURRENCIES.map((currency) => (
                <DropdownMenuItem
                  key={currency.code}
                  onClick={() => handleSelect(currency)}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <span>
                    <span className="font-medium mr-2">{currency.symbol}</span>
                    {currency.code} - {currency.name}
                  </span>
                  {userCurrency?.code === currency.code && (
                    <Check className="h-4 w-4 text-emerald-600" />
                  )}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">All Currencies</DropdownMenuLabel>
            </>
          )}
          
          <ScrollArea className="h-48">
            {filteredCurrencies.map((currency) => (
              <DropdownMenuItem
                key={currency.code}
                onClick={() => handleSelect(currency)}
                className="flex items-center justify-between cursor-pointer"
              >
                <span>
                  <span className="font-medium mr-2">{currency.symbol}</span>
                  {currency.code} - {currency.name}
                </span>
                {userCurrency?.code === currency.code && (
                  <Check className="h-4 w-4 text-emerald-600" />
                )}
              </DropdownMenuItem>
            ))}
          </ScrollArea>
          
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleAutoDetect}
            className="text-blue-600 cursor-pointer"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Auto-detect my location
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Full version for settings page
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Currency Preference</label>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-full justify-between">
            <span>
              <span className="font-medium mr-2">{userCurrency?.symbol || "$"}</span>
              {userCurrency?.code || "USD"} - {ALL_CURRENCIES.find(c => c.code === userCurrency?.code)?.name || "US Dollar"}
            </span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-80">
          <div className="p-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search currency..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <DropdownMenuSeparator />
          <ScrollArea className="h-64">
            {filteredCurrencies.map((currency) => (
              <DropdownMenuItem
                key={currency.code}
                onClick={() => handleSelect(currency)}
                className="flex items-center justify-between cursor-pointer"
              >
                <span>
                  <span className="font-medium mr-2">{currency.symbol}</span>
                  {currency.code} - {currency.name}
                </span>
                {userCurrency?.code === currency.code && (
                  <Check className="h-4 w-4 text-emerald-600" />
                )}
              </DropdownMenuItem>
            ))}
          </ScrollArea>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleAutoDetect}
            className="text-blue-600 cursor-pointer"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Auto-detect based on my location
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {saving && <p className="text-xs text-muted-foreground">Saving preference...</p>}
    </div>
  );
};

export default CurrencySelector;
